/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.auth

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.network.auth.AuthService
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.feature.auth.generated.resources.Res
import studio.oddpods.cauce.feature.auth.generated.resources.otp_error_verify_failed

data class OtpState(
    val email: String,
    val code: String = "",
    val isLoading: Boolean = false,
    val error: UiText? = null,
)

sealed class OtpEvent {
    data object AuthSuccess : OtpEvent()
    data object NavigateBackToLanding : OtpEvent()
}

sealed class OtpAction {
    data class CodeChanged(val code: String) : OtpAction()
    data object VerifyClicked : OtpAction()
    data object ResendClicked : OtpAction()
    data object ChangeEmailClicked : OtpAction()
}

// Resend throttling relies on Supabase's server-side rate limit (4/hour/email). A client-side
// live countdown is deferred — see "Out-of-scope follow-ups" in the design spec.
class OtpViewModel(
    email: String,
    private val authService: AuthService,
) : BaseViewModel<OtpState, OtpEvent, OtpAction>(OtpState(email = email)) {

    override fun handleAction(action: OtpAction) {
        when (action) {
            is OtpAction.CodeChanged -> handleCodeChanged(action.code)
            OtpAction.VerifyClicked -> verify()
            OtpAction.ResendClicked -> resend()
            OtpAction.ChangeEmailClicked -> sendEvent(OtpEvent.NavigateBackToLanding)
        }
    }

    private fun handleCodeChanged(raw: String) {
        val cleaned = raw.filter { it.isDigit() }.take(CODE_LENGTH)
        mutableStateFlow.value = state.copy(code = cleaned, error = null)
        if (cleaned.length == CODE_LENGTH) {
            verify()
        }
    }

    private fun verify() {
        if (state.code.length != CODE_LENGTH || state.isLoading) return

        mutableStateFlow.value = state.copy(isLoading = true, error = null)
        viewModelScope.launch {
            authService.verifyOtp(state.email, state.code).fold(
                onSuccess = {
                    mutableStateFlow.value = state.copy(isLoading = false)
                    sendEvent(OtpEvent.AuthSuccess)
                },
                onFailure = { error ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        code = "",
                        error = error.message?.let { UiText.Dynamic(it) }
                            ?: UiText.Resource(Res.string.otp_error_verify_failed),
                    )
                },
            )
        }
    }

    private fun resend() {
        if (state.isLoading) return

        mutableStateFlow.value = state.copy(isLoading = true, error = null)
        viewModelScope.launch {
            authService.sendOtp(state.email).fold(
                onSuccess = {
                    mutableStateFlow.value = state.copy(isLoading = false)
                },
                onFailure = { error ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        error = error.message?.let { UiText.Dynamic(it) }
                            ?: UiText.Resource(Res.string.otp_error_verify_failed),
                    )
                },
            )
        }
    }

    private companion object {
        private const val CODE_LENGTH = 6
    }
}
